/**
 * A ~100 line history router. SPEC.md §47, §44.
 *
 * Three routes, no nesting, no loaders, no data layer — a routing dependency
 * would add bundle weight for nothing here. §44
 *
 *   /                Landing
 *   /j/:joinCode     Join / reconnect
 *   /s/:sessionId    Active table
 *
 * History and settings are drawers on the table screen, not routes. §47
 */

import { useMemo, useSyncExternalStore } from 'react';
import { idSchema, normalizeJoinCode } from '@card-counter/shared';
import {
  DEFAULT_LOCALE,
  type LocaleCode,
  isLocaleCode,
} from '@/features/tutorial/i18n/locales';

export type Route =
  | { name: 'landing' }
  | { name: 'join'; joinCode: string }
  | { name: 'game'; sessionId: string }
  | { name: 'tutorial'; locale: LocaleCode }
  | { name: 'not-found'; path: string };

export type RouteName = Route['name'];

/** Parses a pathname into a route. Query and hash are ignored. */
export function parseRoute(pathname: string): Route {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return { name: 'landing' };

  if (segments[0] === 'j' && segments.length === 2) {
    // Join codes are case-insensitive and may be pasted with punctuation. §6.2
    const joinCode = normalizeJoinCode(segments[1]!);
    if (joinCode.length > 0) return { name: 'join', joinCode };
  }

  /*
    `/tutorial` and `/tutorial/:locale`. An unrecognised or absent locale falls
    back to English rather than being a dead end: the chapter content is the
    same either way, so a mistyped or retired language code should show
    somebody the tutorial, not a 404. The chapter itself rides in the hash,
    which this parser deliberately ignores — see `TutorialPage`. §34.5
  */
  if (segments[0] === 'tutorial' && segments.length <= 2) {
    const requested = segments[1];
    return {
      name: 'tutorial',
      locale: requested !== undefined && isLocaleCode(requested) ? requested : DEFAULT_LOCALE,
    };
  }

  if (segments[0] === 's' && segments.length === 2) {
    // A session id that isn't an id could never resolve, so it is a dead end
    // rather than a load failure. §34.5
    const parsed = idSchema.safeParse(segments[1]);
    if (parsed.success) return { name: 'game', sessionId: parsed.data };
  }

  return { name: 'not-found', path: pathname };
}

// --- Subscription ------------------------------------------------------------

const listeners = new Set<() => void>();

function currentPathname(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

/**
 * useSyncExternalStore demands a referentially stable snapshot, so the raw
 * pathname string is the store value and parsing happens in a memo above it.
 */
let snapshot = currentPathname();

function notify(): void {
  snapshot = currentPathname();
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener('popstate', notify);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('popstate', notify);
    }
  };
}

function getSnapshot(): string {
  return snapshot;
}

export interface NavigateOptions {
  /** Replace the current entry instead of pushing — used for redirects. */
  replace?: boolean;
}

/**
 * Navigates within the app. pushState/replaceState do not emit popstate, so
 * subscribers are notified explicitly.
 */
export function navigate(path: string, options: NavigateOptions = {}): void {
  if (typeof window === 'undefined') return;
  if (path === window.location.pathname + window.location.search) {
    if (!options.replace) return;
  }
  if (options.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
  notify();
}

/** The current route, re-rendering on navigate() and on back/forward. */
export function useRoute(): Route {
  const pathname = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => parseRoute(pathname), [pathname]);
}

/** Path builders, so no component has to hand-assemble a URL. §47 */
export const paths = {
  landing: () => '/',
  join: (joinCode: string) => `/j/${normalizeJoinCode(joinCode)}`,
  game: (sessionId: string) => `/s/${sessionId}`,
  tutorial: (locale: LocaleCode) => `/tutorial/${locale}`,
} as const;

// --- Dead end ----------------------------------------------------------------

/**
 * Unknown URL. A mistyped or expired link gets a way forward rather than a
 * blank screen. §34.5
 */
export function RouteNotFound() {
  return (
    // Centred when it fits, scrolled when it does not: on a short viewport
    // `justify-center` would put the way out below the fold with nothing to
    // reach it by. §18.2
    <main className="scroll-area flex min-h-0 flex-1 flex-col p-safe">
      <div className="m-auto flex flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <p className="text-5xl" aria-hidden="true">
            🃏
          </p>
          <h1 className="text-2xl font-semibold text-ink">There's nothing here</h1>
          <p className="max-w-xs text-ink-muted">
            That link doesn't point at a table. It may have been mistyped, or the game may have
            finished.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(paths.landing())}
          className="min-h-tap rounded-control bg-accent px-6 py-3 font-semibold text-accent-ink transition-opacity active:opacity-80"
        >
          Start or join a game
        </button>
      </div>
    </main>
  );
}
